import React, { Children, cloneElement } from 'react';
import PropTypes from 'prop-types';
import JustGivingLogin from '@jg/justgiving-app-login/app';
import JustGivingLoginApp from '@jg/justgiving-app-login/app/containers/App';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import Header from 'components/Header/Header';
import config from 'config/config';
import { Loading } from '@jg/components';
import mainActions from 'actions/mainActions/mainActions';
import locationUtils from 'utils/locationUtils/locationUtils';
import {
  getParticipantEmail,
  getParticipantFirstName,
  getParticipantLastName,
  getCharityId,
  getEventId,
} from 'selectors/apiSelectors/apiSelectors';
import { isRequestInFlight } from 'selectors/httpSelectors/httpSelectors';
import analytics from 'utils/analytics/analytics';
import { browserHistory } from 'react-router';
import resources from 'config/resources';
import Tools from 'utils/tools/tools';

import './login.scss';
import './alert.scss';

export const addProps = (elements, props) =>
  Children.map(elements, (element) => cloneElement(element, { ...props }));

export class LoginPage extends React.Component {
  static propTypes = {
    children: PropTypes.element.isRequired,
    push: PropTypes.func,
    facebookLoginStatus: PropTypes.object,
    location: PropTypes.object,
    hostAppProps: PropTypes.object,
    isParticipantInFlight: PropTypes.bool,
    mainActionsBound: PropTypes.object,
    charityId: PropTypes.string,
  }

  static defaultProps = {
    push: browserHistory && browserHistory.push,
    location: locationUtils.locationStub,
    charityId: '',
  }

  getLoginAppProps() {
    const { facebookLoginStatus } = this.props;
    return {
      locale: 'en-gb',
      emailPageContinueButtonLabel: 'Continue',
      registerPageContinueButtonLabel: 'Continue',
      passwordPageContinueButtonLabel: 'Continue',
      loginSuccessCallback: (response) => this.handleUserIsLoggedIn(response),
      emailPageHeading1: (
        <span>
          Nice choice!
          <span className="jg-text--light dna-block">
            You’re almost ready to MATCHmyMILES!
          </span>
        </span>
      ),
      emailPageFacebookAssuranceMessage: (
        <span className="invite-friends__terms">
          <span>By continuing to the next step you are agreeing to the </span>
          <a href="https://www.justgiving.com/info/terms-of-service" target="_blank" rel="noopener noreferrer">terms of service</a>
          <span> of JustGiving</span>
        </span>
      ),
      passwordPageHeading1: 'Welcome back',
      registerPageHeading1: 'Register for MATCHmyMILES',
      globalAlertsDecoratorClass: 'login-alert-donate',
      mainClassName: 'donate-login',
      requestSettings: {
        ClientId: 'jg.web',
        RegistrationSource: 2,
        LongLived: true,
      },
      isAppDisabled: false,
      updateRoute: this.updateRoute,
      facebookLoginStatus,
    };
  }

  getPageContent() {
    const { isParticipantInFlight, hostAppProps } = this.props;
    if (isParticipantInFlight) {
      return <Loading size="large" />;
    }
    return (
      <JustGivingLogin locale="en-GB">
        <JustGivingLoginApp updateRoute={this.updateRoute} hostAppProps={hostAppProps} >
          <div>{ this.childrenWithProps() }</div>
        </JustGivingLoginApp>
      </JustGivingLogin>
    );
  }

  childrenWithProps = () => {
    const { children } = this.props;
    return addProps(children, this.getLoginAppProps());
  }

  updateRoute = (route, queryString) => {
    const { push } = this.props;
    const validRoutes = {
      email: 'email',
      register: 'register',
      password: 'password',
    };
    validRoutes[route] && push(`${config.ROOT_PATH}/login/${validRoutes[route]}${queryString || ''}`);

    Tools.scrollToTop();
  }

  handleUserIsLoggedIn(response) {
    const { accessToken, refreshToken } = response;
    const { push, location, mainActionsBound, charityId } = this.props;
    mainActionsBound.updateAuthAccessToken(accessToken);
    mainActionsBound.updateAuthRefreshToken(refreshToken);
    push(`${config.ROOT_PATH}/invite-friends?guid=${location.query.guid}&charityId=${charityId}`);
  }

  render() {
    return (
      <div className="login">
        <Header step="1.5" />
        <div className="login__body container">
          { this.getPageContent() }
        </div>
      </div>
    );
  }
}

const trackClick = (state, { pageSection, eventValue }) => {
  const location = state.routing.locationBeforeTransitions;
  const participantEmail = getParticipantEmail(state);
  const eventId = getEventId(state);
  const charityId = Number(location.query.charityId);
  analytics.track({
    page_type: 'custom project page',
    event: 'link click',
    subtype: 'button',
    event_value: eventValue,
    page_section: pageSection,
    activity_id: eventId,
    activity_type: 'event_id',
    content_id: charityId,
    content_type: 'charity_id',
    charity_id: charityId,
    email: participantEmail,
    actor_id: location.query.guid,
    actor_sources: 'participant guid',
  });
};

const trackPageView = (state, eventValue, extras) => {
  const location = state.routing.locationBeforeTransitions;
  const participantEmail = getParticipantEmail(state);
  const eventId = getEventId(state);
  const charityId = Number(location.query.charityId);
  analytics.track({
    page_type: 'custom project page',
    event: 'page view',
    subtype: 'match my miles',
    event_value: eventValue,
    activity_id: eventId,
    activity_type: 'event_id',
    content_id: charityId,
    content_type: 'charity_id',
    charity_id: charityId,
    email: participantEmail,
    actor_id: location.query.guid,
    actor_sources: 'participant guid',
    ...extras,
  });
};

const mapStateToProps = (state) => ({
  participantEmail: getParticipantEmail(state),
  eventId: getEventId(state),
  facebookLoginStatus: state.facebookStore.get('facebookLoginStatus').toJS(),
  hostAppProps: {
    emailAddress: getParticipantEmail(state),
    firstName: getParticipantFirstName(state),
    lastName: getParticipantLastName(state),
    fbLoginScope: 'email user_friends',
    onLoginWithFacebookClicked: () => {
      trackClick(state, {
        pageSection: 'match my miles > log in',
        eventValue: 'continue with facebook',
      });
    },
    onLoginWithEmailClicked: () => {
      trackClick(state, {
        pageSection: 'match my miles > log in',
        eventValue: 'continue',
      });
    },
    onRegisterPageSubmitClicked: () => {
      trackClick(state, {
        pageSection: 'match my miles > log in > about you',
        eventValue: 'continue',
      });

      Tools.scrollToTop();
    },
    onEmailPageMount: () => {
      state.mainStore.get('participantPromise').then(() => {
        trackPageView(state, 'log in');
      });
    },
    onPasswordPageMount: () => {
      state.mainStore.get('participantPromise').then(() => {
        trackPageView(state, 'about you', {
          page_section: 'match my miles > log in > about you',
        });
      });
    },
    onRegisterPageMount: () => {
      state.mainStore.get('participantPromise').then(() => {
        trackPageView(state, 'about you', {
          page_section: 'match my miles > log in > about you',
        });
      });
    },
  },
  isParticipantInFlight: isRequestInFlight(resources.participant.requestName)(state),
  charityId: getCharityId(state),
});

export default connect(
  mapStateToProps,
  (dispatch) => ({
    mainActionsBound: bindActionCreators(mainActions, dispatch),
  }),
)(LoginPage);




// WEBPACK FOOTER //
// ./layouts/Login/Login.js